import React from "react";
import Swal from "sweetalert2";
import { setDoc, deleteDoc, doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { CLUB_ID, THEME_COLOR, LEVELS } from "../config/club";
import { useState, useMemo } from "react";
import PlayerMatchHistoryModal from "./admin/PlayerMatchHistoryModal";

export default function PlayersListSection({
    searchTerm, setSearchTerm, players, filteredPlayers,
    sortBy, setSortBy, sortDirection, setSortDirection,
    playerLevels, gamesPlayed, queue, setPlayers,
    playerStatusById, matchHistory, getPlayerLevelMatchStats,
    updatePlayerLevel, levelColor, ToastShow, sessionId,
    teams,
    teamPlayers,
    setTeams,
    setTeamPlayers
}) {
    // 1. Define the state (put this at the top of PlayersModal)
    const [filterStatus, setFilterStatus] = React.useState("all");
    const [showHistory, setShowHistory] = useState(false);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const activeCount = players.filter(p =>
        ['playing', 'pending', 'waiting'].includes(playerStatusById[p.id])
    ).length;

    const inactiveCount = players.filter(p =>
        ['left', 'removed'].includes(playerStatusById[p.id])
    ).length;
    // 2. Add the filtering logic before your .sort()
    const displayPlayers = filteredPlayers.filter(p => {
        const status = playerStatusById[p.id] || "waiting";
        if (filterStatus === "active") {
            return ['playing', 'pending', 'waiting'].includes(status);
        }
        if (filterStatus === "inactive") {
            return ['left', 'removed'].includes(status);
        }
        return true; // "all"
    });
    const playerTeams = useMemo(() => {
        const mapping = {};

        Object.entries(teamPlayers).forEach(([teamId, players]) => {
            players.forEach((player) => {
                mapping[player.id] = teamId;
            });
        });

        return mapping;
    }, [teamPlayers]);
    const assignPlayerToTeam = async (player, newTeamId) => {
        try {
            const currentTeamId = playerTeams[player.id];

            // remove from old team
            if (currentTeamId) {
                await deleteDoc(
                    doc(
                        db,
                        "clubs",
                        CLUB_ID,
                        "sessions",
                        sessionId,
                        "teams",
                        currentTeamId,
                        "players",
                        player.id
                    )
                );
            }

            // if cleared only
            if (!newTeamId) {
                ToastShow("success", `${player.name} removed from team`);
                return;
            }

            // add to new team
            await setDoc(
                doc(
                    db,
                    "clubs",
                    CLUB_ID,
                    "sessions",
                    sessionId,
                    "teams",
                    newTeamId,
                    "players",
                    player.id
                ),
                {
                    playerId: player.id,
                    name: player.name,
                    level: player.level || 1,
                    photoURL: player.photoURL || "",
                    joinedAt: serverTimestamp(),
                }
            );

            ToastShow("success", `${player.name} assigned`);
        } catch (err) {
            console.error(err);
            ToastShow("error", "Failed assigning player");
        }
    };

    return (
        <>
            <div className="mt-4">
                {/* SEARCH & FILTER BAR */}
                <div className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
                    <div className="card-body p-3">
                        <div className="row g-3 align-items-center">
                            <div className="col-md-8">
                                <div className="input-group border rounded-pill px-3 py-1">
                                    <span className="input-group-text bg-transparent border-0"
                                        style={{ color: "var(--text-muted)" }}
                                    >
                                        <i className="bi bi-search"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control bg-transparent border-0 shadow-none"
                                        placeholder="Search players..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="col-md-4">
                                <div className="d-flex justify-content-md-end align-items-center gap-3 flex-wrap">
                                    <div className="d-flex align-items-center gap-3 ms-5 ps-2">
                                        <div className="small fw-bold text-muted d-flex align-items-center">
                                            <i className="bi bi-people-fill me-1 opacity-50"></i>
                                            <span>{players.length} Total</span>
                                        </div>
                                        <div className="vr opacity-25" style={{ height: '12px' }}></div>
                                        <div className="small fw-bold text-success d-flex align-items-center">
                                            <span className="bg-success rounded-circle me-1" style={{ width: '6px', height: '6px' }}></span>
                                            <span>{activeCount} Active</span>
                                        </div>
                                        <div className="vr opacity-25" style={{ height: '12px' }}></div>
                                        <div className="small fw-bold text-secondary d-flex align-items-center">
                                            <span className="bg-secondary rounded-circle me-1" style={{ width: '6px', height: '6px' }}></span>
                                            <span>{inactiveCount} Inactive</span>
                                        </div>
                                    </div>
                                    <div className="vr d-none d-md-block"></div>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="small text-muted fw-bold text-uppercase" style={{ fontSize: '0.65rem' }}>Sort:</span>
                                        <select
                                            className="form-select form-select-sm rounded-pill shadow-none w-auto"
                                            value={sortBy}
                                            onChange={(e) => setSortBy(e.target.value)}
                                            style={{
                                                background: "var(--card-bg)",
                                                color: "var(--text-color)",
                                                border: "1px solid var(--border-color)"
                                            }}
                                        >

                                            <option value="joined">Joined Time</option>
                                            <option value="games">Games</option>
                                            <option value="level">Level</option>
                                            <option value="name">Name</option>
                                        </select>
                                        <button
                                            className="btn btn-sm rounded-circle"
                                            onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                                        >
                                            <i className={`bi bi-sort-numeric-${sortDirection === 'asc' ? 'down' : 'up'}`}></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* STATUS FILTER TABS */}
                <div className="d-flex align-items-center gap-2 mb-3 px-1">
                    {[
                        { id: 'all', label: 'All', count: players.length, color: 'secondary' },
                        { id: 'active', label: 'Active', count: activeCount, color: 'success' },
                        { id: 'inactive', label: 'Inactive', count: inactiveCount, color: 'danger' }
                    ].map(tab => (
                        <button
                            key={`${tab.id}tab-list`}
                            onClick={() => setFilterStatus(tab.id)}
                            className={`btn btn-sm rounded-pill px-3 fw-black ${filterStatus === tab.id
                                ? `btn-${tab.color} shadow`
                                : 'btn-light text-muted border-0'
                                }`}
                            style={{
                                fontSize: "0.7rem",
                                background:
                                    filterStatus === tab.id ? "" : "var(--surface-bg)",
                                color:
                                    filterStatus === tab.id ? "" : "var(--text-muted)"
                            }}
                        >
                            {tab.label} <span className="ms-1 opacity-50">{tab.count}</span>
                        </button>
                    ))}
                </div>
                {/* PLAYER GRID */}
                <div className="overflow-auto pe-2" style={{ maxHeight: "2000px", scrollbarWidth: 'thin' }}>
                    <div className="row g-3">
                        {displayPlayers.length === 0 ? (
                            <div className="col-12 text-center py-5">
                                <i
                                    className="bi bi-person-x fs-1 opacity-25"
                                    style={{ color: "var(--text-muted)" }}
                                ></i>
                                <p className="text-muted mt-2">No players matching your search.</p>
                            </div>
                        ) : (
                            displayPlayers
                                .slice()
                                .sort((a, b) => {
                                    let result = 0;
                                    if (sortBy === "level") result = (a.level || 1) - (b.level || 1);
                                    else if (sortBy === "games") result = (gamesPlayed[a.name] || 0) - (gamesPlayed[b.name] || 0);
                                    else if (sortBy === "joined") {
                                        result = (() => {
                                            const tA = a.joinedAt?.toMillis ? a.joinedAt.toMillis() : null;
                                            const tB = b.joinedAt?.toMillis ? b.joinedAt.toMillis() : null;

                                            if (tA === null && tB === null) return 0;
                                            if (tA === null) return 1;  // treat null as "greater"
                                            if (tB === null) return -1; // so null goes last
                                            return tA - tB;
                                        })();
                                    }
                                    else result = a.name.localeCompare(b.name);
                                    return sortDirection === "asc" ? result : -result;
                                })
                                .map((p) => {
                                    const status = playerStatusById[p.id] || "waiting";
                                    const isInQueue = !!queue.find(q => q.playerId === p.id);
                                    const stats = getPlayerLevelMatchStats(matchHistory, playerLevels, p.name);
                                    const pLevel = p.level || 1;
                                    const joinedTime = p.joinedAt?.toDate
                                        ? p.joinedAt.toDate().toLocaleString("en-US", {
                                            hour: "2-digit",
                                            minute: "2-digit"
                                        })
                                        : null;

                                    return (
                                        <div className="col-6 col-sm-3 col-lg-2 col-xl-2" key={`${p.id}-player-list`}>
                                            <div className="card h-100 border-0 shadow-sm rounded-4 position-relative overflow-hidden transition-all hover-lift"
                                                style={{
                                                    background: "var(--card-bg)",
                                                    color: "var(--text-color)"
                                                }}
                                            >
                                                {/* Level Indicator Top Bar */}
                                                <div style={{ height: '4px', backgroundColor: levelColor(pLevel) }}></div>

                                                <div className="card-body p-3">
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <div>
                                                            <h6 className="fw-bold mb-0 text-truncate" style={{ maxWidth: '110px' }}>{p.name}</h6>
                                                            <small className="text-muted">{gamesPlayed[p.name] || 0} Games</small>
                                                            <hr></hr>
                                                            <small className="text-muted"> Joined @ {joinedTime || ""}</small>
                                                        </div>
                                                        <span
                                                            className="badge rounded-pill"
                                                            style={{
                                                                background: "var(--surface-bg)",
                                                                color: "var(--text-color)",
                                                                border: "1px solid var(--border-color)"
                                                            }}
                                                        >
                                                            Lvl {pLevel}
                                                        </span>
                                                    </div>

                                                    {/* Stats Mini Grid */}
                                                    <div className="d-flex gap-1 mb-3">
                                                        <div className="flex-fill text-center p-1 rounded bg-warning-subtle text-warning-emphasis" style={{ fontSize: '0.65rem' }}>
                                                            <div className="fw-bold">{stats.lowerLevel}</div>
                                                            <div className="opacity-75">Low</div>
                                                        </div>
                                                        <div className="flex-fill text-center p-1 rounded bg-success-subtle text-success-emphasis" style={{ fontSize: '0.65rem' }}>
                                                            <div className="fw-bold">{stats.sameLevel}</div>
                                                            <div className="opacity-75">Mid</div>
                                                        </div>
                                                        <div className="flex-fill text-center p-1 rounded bg-danger-subtle text-danger-emphasis" style={{ fontSize: '0.65rem' }}>
                                                            <div className="fw-bold">{stats.higherLevel}</div>
                                                            <div className="opacity-75">High</div>
                                                        </div>
                                                    </div>

                                                    {/* Level Quick-Shift */}
                                                    {(status === "waiting" || status === "left" || status === "removed") && (
                                                        <>
                                                            <select
                                                                className="form-select form-select-sm mb-3 rounded-3"
                                                                style={{
                                                                    background: "var(--surface-bg)",
                                                                    color: "var(--text-color)",
                                                                    border: "1px solid var(--border-color)"
                                                                }}

                                                                value={pLevel}
                                                                onChange={(e) => updatePlayerLevel(p.id, e.target.value)}
                                                            >
                                                                {LEVELS.map(l => <option key={`${l.value}-change-level-list`} value={l.value}>{l.label}</option>)}
                                                            </select>
                                                            <select
                                                                className="form-select form-select-sm rounded-3"
                                                                value={playerTeams[p.id] || ""}
                                                                onChange={(e) => assignPlayerToTeam(p, e.target.value)}
                                                                style={{
                                                                    background: "var(--surface-bg)",
                                                                    color: "var(--text-color)",
                                                                    border: "1px solid var(--border-color)"
                                                                }}
                                                            >
                                                                <option value="">No Team</option>

                                                                {teams.map((team) => (
                                                                    <option key={team.id} value={team.id}>
                                                                        {team.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </>
                                                    )}
                                                    <hr></hr>
                                                    {playerTeams[p.id] && (
                                                        <small
                                                            className="d-block mt-1 text-success fw-bold"
                                                            style={{ fontSize: "0.65rem" }}
                                                        >
                                                            Assigned to team
                                                        </small>
                                                    )}
                                                    {/* Action Buttons */}
                                                    <div className="d-grid gap-2">
                                                        {/* ✅ NEW BUTTON */}
                                                        <button
                                                            className="btn btn-sm btn-outline-secondary rounded-pill fw-bold"
                                                            style={{ fontSize: "0.7rem" }}
                                                            //onClick={() => showPlayerMatches(p, matchHistory, levelColor)}
                                                            onClick={() => {
                                                                setSelectedPlayer(p);
                                                                setShowHistory(true);
                                                            }}
                                                        >
                                                            <i className="bi bi-clock-history me-1"></i> View Matches
                                                        </button>
                                                        {(status === "waiting" || status === "left" || status === "removed") ? (
                                                            <>
                                                                {isInQueue ? (
                                                                    <button
                                                                        className="btn btn-sm btn-soft-danger rounded-pill fw-bold"
                                                                        onClick={() => handleRemoveFromQueue(p, sessionId, ToastShow)}
                                                                    >
                                                                        <i className="bi bi-person-dash me-1"></i> Exit Queue
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        className="btn btn-sm btn-soft-primary rounded-pill fw-bold"
                                                                        style={{ backgroundColor: `${THEME_COLOR}15`, color: THEME_COLOR }}
                                                                        onClick={() => handleAddToQueue(p, sessionId, ToastShow)}
                                                                    >
                                                                        <i className="bi bi-person-plus me-1"></i> Join Queue
                                                                    </button>
                                                                )}

                                                                <button
                                                                    className="btn btn-link btn-sm text-danger text-decoration-none p-0 opacity-50 hover-opacity-100"
                                                                    style={{ fontSize: "0.7rem" }}
                                                                    onClick={() => handleDeletePlayer(p, isInQueue, sessionId, setPlayers, ToastShow)}
                                                                >
                                                                    Delete Player
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <div className="text-center py-2">
                                                                <span className={`badge w-100 py-2 rounded-pill ${status === "playing" ? "bg-success" : "bg-warning text-dark"}`}>
                                                                    {status.toUpperCase()}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>

                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                        )}
                    </div>
                </div>

                {/* Hover Effects CSS */}
                <style>{`
                .hover-lift:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 24px rgba(0,0,0,0.15) !important;
}
                .btn-soft-danger { background-color: #ff4d4d15; color: #ff4d4d; border: none; }
                .btn-soft-danger:hover { background-color: #ff4d4d; color: white; }
                .hover-opacity-100:hover { opacity: 1 !important; }
            `}</style>
            </div>
            <PlayerMatchHistoryModal
                show={showHistory}
                onClose={() => setShowHistory(false)}
                player={selectedPlayer}
                matchHistory={matchHistory}
                levelColor={levelColor}
            />
        </>
    );
}

// Extract Logic Functions for cleaner JSX (You can keep these inside or move to a helper file)
async function handleAddToQueue(p, sessionId, ToastShow) {
    try {
        let username = null;
        let photoLink = null;
        if (p.username) {
            username = p.username;
        } if (p.photoURL) {
            photoLink = p.photoURL;
        }
        await setDoc(doc(db, "clubs", CLUB_ID, "sessions", sessionId, "queue", p.id), {
            playerId: p.id, name: p.name, username: username, photoURL: photoLink, level: p.level || 1,
            joinedAt: serverTimestamp(), waitingSince: p.waitingSince || Date.now(),
        });
        await updateDoc(doc(db, "clubs", CLUB_ID, "sessions", sessionId, "players", p.id), {
            status: "waiting"
        });
        ToastShow("success", `${p.name} added to queue`);
    } catch (err) { ToastShow("error", "Add failed"); }
}

async function handleRemoveFromQueue(p, sessionId, ToastShow) {
    const result = await Swal.fire({ title: `Remove ${p.name}?`, icon: "warning", showCancelButton: true });
    if (!result.isConfirmed) return;
    try {
        await deleteDoc(doc(db, "clubs", CLUB_ID, "sessions", sessionId, "queue", p.id));
        await updateDoc(doc(db, "clubs", CLUB_ID, "sessions", sessionId, "players", p.id), {
            status: "removed"
        });
        ToastShow("success", "Removed from queue");
    } catch (err) { ToastShow("error", "Remove failed"); }
}

async function handleDeletePlayer(p, isInQueue, sessionId, setPlayers, ToastShow) {
    const result = await Swal.fire({ title: `Permanently delete ${p.name}?`, text: "This cannot be undone.", icon: "error", showCancelButton: true });
    if (!result.isConfirmed) return;
    try {
        const deletes = [deleteDoc(doc(db, "clubs", CLUB_ID, "sessions", sessionId, "players", p.id))];
        if (isInQueue) deletes.push(deleteDoc(doc(db, "clubs", CLUB_ID, "sessions", sessionId, "queue", p.id)));
        await Promise.all(deletes);
        setPlayers((prev) => prev.filter((x) => x.id !== p.id));
        ToastShow("success", "Player deleted");
    } catch (err) { ToastShow("error", "Delete failed"); }
}
function showPlayerMatches(player, matchHistory, levelColor) {
    const matches = matchHistory
        .filter(m =>
            m.match?.some(p => p.name === player.name)
        )
        .sort((a, b) => {
            const tA = a.startAt?.toMillis?.() ?? 0;
            const tB = b.startAt?.toMillis?.() ?? 0;
            return tB - tA; // latest first
        });

    if (matches.length === 0) {
        Swal.fire({
            icon: "info",
            title: `${player.name}`,
            text: "No matches played yet.",
        });
        return;
    }

    Swal.fire({
        title: `<div class="fw-black text-uppercase italic" style="font-size: 1.5rem; color: #333; letter-spacing: 1px;">
            <i class="bi bi-person-badge me-2" style="color: ${THEME_COLOR}"></i>${player.name}
          </div>
          <div style="font-size: 0.8rem; font-weight: 600; color: #666; margin-top: 5px;">PLAYER MATCH HISTORY</div>`,
        width: 750, // Wider for a more premium, non-compact feel
        padding: '2rem',
        html: `
    <div style="max-height: 550px; overflow-y: auto; overflow-x: hidden; padding: 10px;">
      ${matches.map((m, i) => {
            const teamA = [m.match[0], m.match[1]];
            const teamB = [m.match[2], m.match[3]];
            const score = m.score || "PENDING";

            // Function to render a player row with highlight logic
            const renderPlayer = (p) => {
                const isTargetPlayer = p?.name === player.name;
                return `
            <div class="d-flex align-items-center gap-2 p-2 mb-1 rounded-3 ${isTargetPlayer ? 'border border-2' : 'border-0'}" 
                 style="${isTargetPlayer ? `background-color: ${THEME_COLOR}10; border-color: ${THEME_COLOR} !important;` : 'background-color: #fcfcfc;'}">
              <span class="badge rounded-circle d-flex align-items-center justify-content-center" 
                    style="background-color: ${levelColor(p?.level)}; color: #333; width: 24px; height: 24px; font-size: 0.8rem; font-weight: 800;">
                ${p?.level || 0}
              </span>
              <span class="${isTargetPlayer ? 'fw-black' : 'fw-bold'}" 
                    style="font-size: 1rem; color: ${isTargetPlayer ? THEME_COLOR : '#444'}">
                ${p?.name || 'Unknown'}
              </span>
              ${isTargetPlayer ? `<i class="bi bi-star-fill ms-auto" style="color: ${THEME_COLOR}; font-size: 0.8rem;"></i>` : ''}
            </div>
          `;
            };

            return `
          <div class="mb-4 p-4 bg-white border rounded-4 shadow-sm">
            <div class="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
              <span class="badge bg-dark text-white px-3 py-2" style="font-size: 0.8rem; letter-spacing: 1px;">
                MATCH #${matches.length - i}
              </span>
              <div class="text-end">
                <div class="fw-bold text-dark" style="font-size: 0.85rem;">
                  <i class="bi bi-calendar3 me-2"></i>${m.createdAt?.toDate?.().toLocaleDateString() || ""}
                </div>
                <div class="text-muted" style="font-size: 0.75rem;">
                  <i class="bi bi-clock me-2"></i>${m.createdAt?.toDate?.().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) || ""}
                </div>
              </div>
            </div>

            <div class="row align-items-center">
              <div class="col-5">
                <div class="small fw-black text-muted text-uppercase mb-2 italic">Team A</div>
                ${teamA.map(p => renderPlayer(p)).join('')}
              </div>

              <div class="col-2 text-center">
                <div class="display-6 fw-black italic text-dark" style="font-size: 1.8rem; line-height: 1;">
                  ${score}
                </div>
                <hr style="width: 40%; margin: 10px auto; border-top: 2px solid #ddd;">
                <div class="fw-black text-muted italic" style="font-size: 0.8rem;">VS</div>
              </div>

              <div class="col-5">
                <div class="small fw-black text-muted text-uppercase mb-2 italic text-end">Team B</div>
                ${teamB.map(p => renderPlayer(p)).join('')}
              </div>
            </div>
          </div>
        `;
        }).join("")}
    </div>
  `,
        confirmButtonText: "BACK TO DASHBOARD",
        confirmButtonColor: THEME_COLOR,
        customClass: {
            confirmButton: 'rounded-pill px-5 py-2 fw-black italic shadow-sm'
        }
    });
}